import { useEffect, useRef, useState } from "react";

import { RENEWAL_MONTH_OPTIONS, previewRenewalExpiry } from "./subscriptionRenewal";
import "./ConfirmDialog.css";
import "./RenewSubscriptionDialog.css";

/**
 * Renewal dialog for a subscriber, on the same shell as {@link ConfirmDialog}.
 *
 * Renewal is not a yes/no confirmation — the admin picks a term first — so the
 * dialog shows the expiry date the chosen term produces before it can be
 * committed. A lapsed subscription restarts from today rather than extending a
 * date already in the past, and the dialog says which of the two is happening.
 *
 * The preview is a courtesy: the server recalculates the date from the stored
 * subscriber and rejects any month count outside the offered list.
 *
 * @param {{request: {id: string, name: string, currentExpiry: string, status: string}|null, isSaving?: boolean, onConfirm: (months: number) => void, onCancel: () => void}} props
 */
function RenewSubscriptionDialog({ request, isSaving = false, onConfirm, onCancel }) {
  const [months, setMonths] = useState(RENEWAL_MONTH_OPTIONS[0]);
  const selectRef = useRef(null);

  // Every renewal starts from the shortest term rather than inheriting the
  // last one, so a stale selection cannot be confirmed by accident.
  useEffect(() => {
    if (request) setMonths(RENEWAL_MONTH_OPTIONS[0]);
  }, [request]);

  // Escape cancels, and focus lands on the term selector — the one choice the
  // dialog exists to collect.
  useEffect(() => {
    if (!request) {
      return undefined;
    }

    selectRef.current?.focus();

    function onKeyDown(event) {
      if (event.key === "Escape") {
        onCancel();
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [request, onCancel]);

  if (!request) {
    return null;
  }

  const isExpired = request.status !== "ACTIVE";
  const newExpiry = previewRenewalExpiry(request.currentExpiry, months);

  return (
    <div className="confirm-backdrop" onClick={onCancel}>
      <div
        className="confirm-dialog renew-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="renew-dialog-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="renew-dialog-title">Renew subscription</h2>
        <p>
          {request.name} — currently {isExpired ? "expired" : "active"}, expiring{" "}
          {request.currentExpiry}.
        </p>

        <label className="renew-field" htmlFor="renew-months">
          <span>Renew for</span>
          <select
            id="renew-months"
            ref={selectRef}
            value={months}
            disabled={isSaving}
            onChange={(event) => setMonths(Number(event.target.value))}
          >
            {RENEWAL_MONTH_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option} {option === 1 ? "month" : "months"}
              </option>
            ))}
          </select>
        </label>

        <div className="renew-preview">
          <span className="renew-preview__label">New expiry date</span>
          <strong className="renew-preview__value">{newExpiry || "—"}</strong>
          <span className="renew-preview__note">
            {isExpired
              ? "Counted from today, because the subscription has already expired."
              : "Added to the current expiry date, so no remaining days are lost."}
          </span>
        </div>

        <div className="confirm-actions">
          <button type="button" className="confirm-btn" disabled={isSaving} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="confirm-btn is-primary"
            disabled={isSaving || !newExpiry}
            onClick={() => onConfirm(months)}
          >
            {isSaving ? "Renewing…" : "Confirm Renewal"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RenewSubscriptionDialog;
